import { HttpResponse } from 'msw';
import { faker } from '@faker-js/faker';
import { generateFromEnum, generateInitialPaginationApi } from '@ingenico/api-sdk-core/mocks';
import {
  provisioningEntityTypeEnum,
  type ArtifactCapabilitiesResponseApi,
  type ArtifactResourceReadV2Api,
  type ArtifactResourceV2Api,
  type CreatorApi,
  type DownloadLinkResourceApi,
  type ParentEntityReadOnlyApi,
  type ParentEntityRefApi,
  type ResourceDataFileResourceReadApi,
  type ResourceDataFileResourceReadBffApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { MOCKED_USER_ORGANIZATION_ID } from '../../directory-private-local/directory-private-local';

export function generateArtifactResourceReadV2Api(
  opt?: Partial<ArtifactResourceReadV2Api>,
): Required<ArtifactResourceReadV2Api> {
  const mimeType = faker.helpers.arrayElement([
    'application/pdf',
    'image/png',
    'video/mp4',
    'audio/mp3',
    'image/jpeg',
    'image/jpg',
    'text/csv',
    'application/zip',
    'application/gzip',
    'application/tar',
  ]);
  const extension = mimeType.split('/')[1];
  const filename = `resource-${faker.number.int()}.${extension}`;
  return {
    checksum: 'checksum',
    componentType: 'OTHER',
    createdBy: faker.string.uuid(),
    creationDate: faker.date.past(),
    fileName: filename,
    extension,
    id: faker.string.uuid(),
    mimeType: mimeType,
    size: faker.number.int({ min: 150_000, max: 12_000_000 }),
    type: 'TEMPLATE',
    ...opt,
  };
}

function generateParentEntityReadOnlyApi(opt?: Partial<ParentEntityReadOnlyApi>): Required<ParentEntityReadOnlyApi> {
  return {
    id: faker.string.uuid(),
    type: generateFromEnum(provisioningEntityTypeEnum),
    ...opt,
  };
}

function generateResource(index: number): Required<ResourceDataFileResourceReadApi> {
  return {
    id: faker.string.uuid(),
    name: `Resource ${index}`,
    description: faker.commerce.productDescription(),
    creationDate: faker.date.recent({ days: 60 }),
    createdBy: faker.string.uuid(),
    owner: generateParentEntityReadOnlyApi(),
    artifact: generateArtifactResourceReadV2Api(),
    updateDate: faker.date.recent({ days: 30 }),
    updatedBy: faker.string.uuid(),
  };
}

function generateCreatorApi(opt?: Partial<CreatorApi>): Required<CreatorApi> {
  return {
    id: faker.string.uuid(),
    name: `${faker.person.firstName()} ${faker.person.lastName()}`,
    ...opt,
  };
}

function generateParentEntityRefApi(opt?: Partial<ParentEntityRefApi>): Required<ParentEntityRefApi> {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    type: generateFromEnum(provisioningEntityTypeEnum),
    ...opt,
  };
}

function generateArtifactResourceV2Api(opt?: Partial<ArtifactResourceV2Api>): Required<ArtifactResourceV2Api> {
  return generateArtifactResourceReadV2Api(opt);
}

function generateResourceDataFileResourceReadBffApi(
  opt?: Partial<ResourceDataFileResourceReadBffApi>,
): Required<ResourceDataFileResourceReadBffApi> {
  return {
    id: faker.string.uuid(),
    name: faker.commerce.productName(),
    description: faker.commerce.productDescription(),
    creationDate: faker.date.recent({ days: 60 }),
    artifact: generateArtifactResourceV2Api(),
    updateDate: faker.date.recent({ days: 30 }),
    createdBy: generateCreatorApi(),
    parentEntityRef: generateParentEntityRefApi(),
    updatedBy: generateCreatorApi(),
    ...opt,
  };
}

export function generateSearchResourcesV2ResponseApi(count = 25) {
  return {
    pagination: generateInitialPaginationApi(count),
    data: faker.helpers.multiple((_, index) => generateResource(index + 1), { count }),
  };
}

export function generateResourceDataFileResourceListResponseBffApi(count = 50) {
  return {
    pagination: { ...generateInitialPaginationApi(count * 2), itemsPerPage: 50 },
    data: faker.helpers.multiple(
      (_, index) =>
        generateResourceDataFileResourceReadBffApi({
          parentEntityRef: generateParentEntityRefApi({
            id: faker.helpers.arrayElement([MOCKED_USER_ORGANIZATION_ID, faker.string.uuid()]),
          }),
        }),
      { count },
    ),
  };
}

export function searchResourcesV2CustomHandler() {
  return HttpResponse.json(generateSearchResourcesV2ResponseApi());
}
export function searchBffResourcesV2CustomHandler() {
  return HttpResponse.json(generateResourceDataFileResourceListResponseBffApi(50));
}

export function getResourceDownloadLinkV2CustomHandler() {
  return HttpResponse.json<DownloadLinkResourceApi>({ downloadLink: '/mock-resource.json' });
}

export function getResourceSearchFieldValuesV2CustomHandler() {
  return HttpResponse.json(['png', 'jpg', 'pdf', 'csv', 'mp4']);
}

export function generateArtifactCapabilitiesResponseApi(): ArtifactCapabilitiesResponseApi {
  return {
    upload: {
      componentTypes: [
        {
          componentType: 'RESOURCE',
          supportedFileTypes: [
            {
              extension: 'json',
              maxFileSize: 10 * 1024 * 1024,
              mediaTypes: ['application/json'],
            },
            {
              extension: 'csv',
              maxFileSize: 10 * 1024 * 1024,
              mediaTypes: ['text/csv'],
            },
          ],
        },
      ],
    },
  };
}

export function getArtifactCapabilitiesV2CustomHandler() {
  return HttpResponse.json(generateArtifactCapabilitiesResponseApi());
}

export function createArtifactV2CustomHandler() {
  return HttpResponse.json(
    generateArtifactResourceReadV2Api({
      componentType: 'RESOURCE',
      type: 'DATA_FILE',
      extension: 'json',
      mimeType: 'application/json',
      fileName: `resource-${faker.string.alphanumeric(8).toLowerCase()}.json`,
    }),
  );
}

export function createResourceV2CustomHandler() {
  return HttpResponse.json(generateResource(1));
}

export function updateResourceByIdV2Mock() {
  return HttpResponse.json<ResourceDataFileResourceReadApi>(generateResource(0));
}
